/**
 * Per-collection view mode preference (chronological vs grid).
 *
 * Client-only, persisted to localStorage. When a user toggles the
 * collection-detail header's view mode, the choice is keyed by
 * `collectionId` and restored next time they open that collection.
 * Reverting to the default ("chronological") drops the entry so the
 * map stays sparse — only collections that have been *intentionally*
 * switched away from the default occupy storage.
 *
 * Mirrors the shape of `app-tabs-store.ts` (file-local module state +
 * `useSyncExternalStore` + a logger-based error path) so the React
 * side gets cross-tab updates for free.
 *
 * TODO(persistence): Port this to a per-user-per-collection preference
 * table on the backend so the choice syncs across devices. The current
 * localStorage-only implementation is a UX placeholder — fine while
 * the toggle lives on a single surface, but a real preference store
 * should follow the user, not the browser. Likely shape: a
 * `UserCollectionPreference` table keyed `(userId, collectionId)`,
 * with an optimistic-update path on the client. The exported store
 * surface below is the intended API; only the load / save internals
 * would change.
 */
import { useSyncExternalStore } from "react";
import { ConfiguredLogger } from "@/lib/logger";
const logger = new ConfiguredLogger({ caller: "CollectionViewModeStore" });
const STORAGE_KEY = "explorer.collectionViewMode";
const DEFAULT_MODE = "chronological";
let _modes = loadFromStorage();
const _listeners = new Set();
function isViewMode(value) {
    return value === "chronological" || value === "grid";
}
function loadFromStorage() {
    if (typeof window === "undefined")
        return {};
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }
        const out = {};
        for (const [k, v] of Object.entries(parsed)) {
            if (isViewMode(v))
                out[k] = v;
        }
        return out;
    }
    catch (e) {
        logger.warn("Failed to load collection view modes", {
            error: e instanceof Error ? e.message : String(e),
        });
        return {};
    }
}
function saveToStorage(modes) {
    if (typeof window === "undefined")
        return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(modes));
    }
    catch (e) {
        logger.warn("Failed to persist collection view modes", {
            error: e instanceof Error ? e.message : String(e),
        });
    }
}
function notify() {
    for (const l of _listeners)
        l();
}
function subscribe(listener) {
    _listeners.add(listener);
    return () => {
        _listeners.delete(listener);
    };
}
function getMode(collectionId) {
    return _modes[collectionId] ?? DEFAULT_MODE;
}
function setMode(collectionId, mode) {
    const current = getMode(collectionId);
    if (current === mode)
        return;
    const next = { ..._modes };
    if (mode === DEFAULT_MODE) {
        // Sparse map: a collection sitting at the default takes up zero
        // bytes. This keeps the persisted blob small no matter how many
        // collections a power user accumulates.
        delete next[collectionId];
    }
    else {
        next[collectionId] = mode;
    }
    _modes = next;
    saveToStorage(_modes);
    notify();
}
// Cross-tab sync: the `storage` event fires on *other* tabs (not the
// originating tab), so this hook is what keeps multiple windows of the
// same collection aligned. Skipped on SSR / non-browser contexts.
if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
        if (e.key !== STORAGE_KEY)
            return;
        _modes = loadFromStorage();
        notify();
    });
}
export const collectionViewModeStore = {
    /**
     * Reactive read of the view mode for a given collection. Components
     * re-render automatically when the mode changes (in this tab or
     * another).
     */
    useMode(collectionId) {
        return useSyncExternalStore(subscribe, () => getMode(collectionId));
    },
    /**
     * Imperative read for non-React callers. Prefer `useMode` from
     * components.
     */
    getMode,
    setMode,
};
